import { useState, useMemo, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { MagnifyingGlass, CaretDown, Check } from '@phosphor-icons/react';

/**
 * Searchable course picker.
 *
 * - Renders a trigger styled to match the other Select boxes on the form.
 * - On open, shows a search input pinned to the top of a scrollable list.
 * - Shows ~7 courses initially; filtering reveals matches by course_name / course_code.
 * - Empty/loading states are surfaced inline.
 *
 * Props:
 *   • value         - selected course id (string)
 *   • onChange      - (id) => void
 *   • courses       - array of { id, course_name, course_code }
 *   • placeholder   - placeholder shown when no value (default: 'Select course')
 *   • disabled      - boolean
 *   • visibleCount  - number of rows to show before scrolling (default: 7)
 *   • testid        - data-testid applied to the trigger
 */
export default function SearchableCourseSelect({
  value,
  onChange,
  courses = [],
  placeholder = 'Select course',
  disabled = false,
  visibleCount = 7,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const selected = useMemo(
    () => courses.find(c => String(c.id) === String(value)) || null,
    [courses, value],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return courses;
    return courses.filter(c => {
      const name = String(c.course_name || '').toLowerCase();
      const code = String(c.course_code || '').toLowerCase();
      return name.includes(term) || code.includes(term);
    });
  }, [courses, q]);

  // Row height ~ h-10 (40px). Match max-h to visibleCount.
  const rowPx = 40;
  const maxH = `${rowPx * visibleCount}px`;

  const triggerLabel = selected
    ? (selected.course_code
        ? `${selected.course_name || selected.course_code} (${selected.course_code})`
        : (selected.course_name || selected.course_code || String(selected.id)))
    : courses.length === 0
    ? 'No courses configured for this campus'
    : placeholder;

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          title={selected ? (selected.course_name || selected.course_code || '') : ''}
          className="flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-[#E2E8E5] bg-transparent px-3 py-2 text-sm text-left disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20"
        >
          <span
            className={`flex-1 min-w-0 truncate ${selected ? 'text-[#1C1917]' : 'text-[#A8A29E]'}`}
          >
            {triggerLabel}
          </span>
          <CaretDown size={16} className="text-[#6C7A75] shrink-0" weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl border-[#E2E8E5] shadow-lg"
        align="start"
        sideOffset={6}
      >
        <div className="flex items-center gap-2 border-b border-[#E2E8E5] px-3 py-2">
          <MagnifyingGlass size={16} className="text-[#6C7A75] shrink-0" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search courses..."
            autoFocus
            data-testid={testid ? `${testid}-search` : 'course-search'}
            className="h-8 border-0 px-0 py-0 shadow-none focus-visible:ring-0 text-sm placeholder:text-[#A8A29E]"
          />
        </div>
        <div
          className="overflow-y-auto py-1"
          style={{ maxHeight: maxH }}
          data-testid={testid ? `${testid}-list` : 'course-list'}
        >
          {filtered.length === 0 ? (
            <div
              className="px-3 py-4 text-xs text-[#6C7A75] text-center"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              {courses.length === 0 ? 'No courses for this campus.' : 'No matches.'}
            </div>
          ) : (
            filtered.map((c) => {
              const isSelected = String(c.id) === String(value);
              const label = c.course_name || c.course_code || String(c.id);
              const hasCode = !!c.course_code;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange?.(c.id);
                    setOpen(false);
                  }}
                  data-testid={testid ? `${testid}-option-${c.id}` : `course-option-${c.id}`}
                  title={`${label}${hasCode ? ` (${c.course_code})` : ''}`}
                  className={`flex w-full items-start justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-[#F5F0E8] transition-colors ${
                    isSelected ? 'bg-[#FCFAF6]' : ''
                  }`}
                  style={{ minHeight: rowPx }}
                >
                  <span className="flex-1 min-w-0 text-[#1C1917] leading-snug break-words">
                    {label}
                    {hasCode && (
                      <span className="text-[#A8A29E] ml-1">({c.course_code})</span>
                    )}
                  </span>
                  {isSelected && (
                    <Check size={16} className="text-[#0A2540] shrink-0 mt-0.5" weight="bold" />
                  )}
                </button>
              );
            })
          )}
        </div>
        {courses.length > visibleCount && filtered.length > visibleCount && (
          <div
            className="border-t border-[#E2E8E5] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#A8A29E]"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          >
            Scroll for more • {filtered.length} matches
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
